<template>
  <tr>
    <td>{{ props.journey.vref }}</td>
    <td>{{ formatTime(props.journey.time) }}</td>
    <td>{{ props.journey.evtdata?.vmfields?.LineRef }}</td>
    <td>{{ props.journey.evtdata?.Description }}</td>
    <td>{{ props.journey.guid }}</td>
    <td>
      <table>
        <tbody>
          <tr>
            <td><button @click="removeJourney">Remove</button></td>
            <td><button ref="stopsBtn" @click="toggleStops" class="btn-off">Stops</button></td>
            <td><button ref="routeBtn" @click="toggleRoutes" class="btn-off">Route</button></td>
            <td><button ref="legsBtn" @click="toggleLegs" class="btn-off">Legs</button></td>
            <td><button ref="aveRoutesBtn" @click="toggleAveRoutes" class="btn-off">Ave Routes</button></td>
            <td><button ref="fitBtn" @click="fitToScreen">Fit</button></td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script setup>
import { defineProps, ref, onBeforeUnmount, inject } from 'vue';
import { useMapStore } from '@/mapStore';
import L from 'leaflet';

const dataStore = inject('datastore');
const mapStore = useMapStore();

const stopsBtn = ref(null);
const routeBtn = ref(null);
const legsBtn = ref(null);
const aveRoutesBtn = ref(null);
const fitBtn = ref(null);

let plotStops = false
let plotRoute = false
let plotLegs = false
let plotAveRoutes = false

let stopslayer
let routelayer
let legslayer
let poslayer
let averouteslayer

let stopsCol
let routeCol
let legsCol

let routeData

let legsData
let aveRoutesData

onBeforeUnmount(() => {
  console.log('Component is about to be removed');
  hideStops();
  hideRoute();
  hideLegs();
  hideAveRoutes();
});

const props = defineProps({
  journey: {
    type: Object,
    required: true
  }
});


function calculateBearing(lat1, lon1, lat2, lon2) {
  // Convert from degrees to radians
  function toRadians(deg) {
    return deg * (Math.PI / 180);
  }

  function toDegrees(rad) {
    return rad * (180 / Math.PI);
  }

  var phi1 = toRadians(lat1);
  var phi2 = toRadians(lat2);
  var deltaLambda = toRadians(lon2 - lon1);

  var y = Math.sin(deltaLambda) * Math.cos(phi2);
  var x = Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);

  var theta = Math.atan2(y, x);
  theta = toDegrees(theta);  // Convert radians to degrees

  // Normalize the result to a bearing from 0 to 360 degrees
  return (theta + 360) % 360;
}


function formatTime(tm) {
  const date = new Date(tm * 1000);
  const formattedDate = date.toLocaleString();
  return formattedDate
}

function posPopupText(evt) {
  let str = `<b>Time</b>: ${formatTime(evt.time)}</br><b>lat</b>: ${(evt.gps.lat)}</br><b>lon</b>: ${(evt.gps.lon)}</br>`

  return str;
}

function stopPopupText(index, stop) {
  let str =
    `<b>Stop</b>: ${index}</br> 
    <b>Ref</b>: ${stop.reference}</br> 
    <b>Name</b>: ${stop.commonname}</br>
    <b>Locality</b>: ${(stop.localityName)}</br>
    <b>indicator</b>: ${(stop.indicator)}</br>`

  return str;
}

function removeJourney() {
  console.log(`removeJourney ${props.journey}`)
  dataStore.removeJourneyFromWorking(props.journey)
}

function toggleStops() {
  plotStops = !plotStops
  if (plotStops) {
    stopsBtn.value.classList.remove('btn-off');
    stopsBtn.value.classList.add('btn-on');
    showStops()
  } else {
    stopsBtn.value.classList.remove('btn-on');
    stopsBtn.value.classList.add('btn-off');
    hideStops();
  }
}

function toggleRoutes() {
  plotRoute = !plotRoute
  if (plotRoute) {
    routeBtn.value.classList.remove('btn-off');
    routeBtn.value.classList.add('btn-on');
    showRoute();
  } else {
    routeBtn.value.classList.remove('btn-on');
    routeBtn.value.classList.add('btn-off');
    hideRoute();
  }
}

function toggleLegs() {
  plotLegs = !plotLegs
  if (plotLegs) {
    legsBtn.value.classList.remove('btn-off');
    legsBtn.value.classList.add('btn-on');
    showLegs();
  } else {
    legsBtn.value.classList.remove('btn-on');
    legsBtn.value.classList.add('btn-off');
    hideLegs();
  }
}

function toggleAveRoutes() {
  plotAveRoutes = !plotAveRoutes
  if (plotAveRoutes) {
    aveRoutesBtn.value.classList.remove('btn-off');
    aveRoutesBtn.value.classList.add('btn-on');
    showAveRoutes();
  } else {
    aveRoutesBtn.value.classList.remove('btn-on');
    aveRoutesBtn.value.classList.add('btn-off');
    hideAveRoutes();
  }
}

function fitToScreen() {

  let tlLat
  let tlLon
  let brLat
  let brLon

  const stops = props.journey.evtdata?.stops
  if (stops && stops.length > 0) {
    for (let stop of props.journey.evtdata?.stops) {
      if (!tlLat || tlLat > stop.lat) {
        tlLat = stop.lat
      }
      if (!tlLon || tlLon > stop.lon) {
        tlLon = stop.lon
      }

      if (!brLat || brLat < stop.lat) {
        brLat = stop.lat
      }
      if (!brLon || brLon < stop.lon) {
        brLon = stop.lon
      }
    }

    mapStore.fitBounds([tlLat, tlLon], [brLat, brLon]);
  }

}

function showStops() {
  if (!stopslayer) {
    stopslayer = L.layerGroup();

    stopsCol = getRandomVisibleColor()

    // Define the line coordinates
    const lines = [];
    const stops = props.journey.evtdata?.stops
    if (stops) {
      for (let stop of props.journey.evtdata?.stops) {
        lines.push([stop.lat, stop.lon]);
      }

      // Add lines to the layer
      const polyline = L.polyline(lines, { color: stopsCol });
      polyline.addTo(stopslayer);

      // add markers for the stops
      for (const [index, stop] of props.journey.evtdata?.stops.entries()) {

        const marker = L.circleMarker([stop.lat, stop.lon], {
          radius: 12,
          color: 'black',
          fillColor: stopsCol,
          fillOpacity: 1
        }).bindTooltip(stopPopupText(index + 1, stop), { sticky: false });

        marker.addTo(stopslayer);
      }

    }

  }
  mapStore.addLayer(stopslayer);

}

/* eslint-disable */
async function showRoute() {
  try {
    if (!routelayer) {

      console.log("Build Route layer")

      routeCol = getRandomVisibleColor()

      if (!routeData) {

        let evtData = await dataStore.retrieveJourneyEvents(props.journey.guid)
        routeData = [];
        for (let evt of evtData) {
          if (evt.event === "pos") {
            routeData.push(evt);
          }
        }

      }
      routelayer = L.layerGroup();

      // Define the line coordinates
      const lines = [];

      for (let evt of routeData) {
        lines.push([evt.gps?.lat, evt.gps?.lon]);
      }

      // Add lines to the layer
      const polyline = L.polyline(lines, { color: routeCol });
      polyline.addTo(routelayer);

      const arrowIcon = (fillColor = 'red') => L.divIcon({
        className: 'arrow-icon',
        html: `
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <polygon points="10,0 20,20 10,15 0,20" fill="${fillColor}" />
        </svg>
    `,
        iconSize: [20, 20],  // Adjust the size of the icon
        iconAnchor: [10, 10],  // Center the icon
      });

      // add markers for the pos events
      let showPositions = true
      let lastBearing = 90
      if (showPositions) {
        poslayer = L.layerGroup();
        for (let i = 0; i < routeData.length; i++) {
          let evt = routeData[i];
          let nevt = routeData[i + 1]

          let bearing = lastBearing
          if (nevt) {
            bearing = calculateBearing(evt.gps?.lat, evt.gps?.lon, nevt.gps?.lat, nevt.gps?.lon)
            lastBearing = bearing
          }

          let point = [evt.gps?.lat, evt.gps?.lon]
          const marker = L.marker(point, {
            icon: arrowIcon(routeCol),
            color: 'black',
            rotation: bearing
          }).bindTooltip(posPopupText(evt), { sticky: false });

          marker.addTo(poslayer);
        }
      }
    } else {
      console.log("Route layer alread exists")
    }

    if (routelayer) mapStore.addLayer(routelayer);
    if (poslayer) mapStore.addLayer(poslayer);

  }
  catch (error) {
    console.log(`Error fetching route data: ${error}`)
  }

}

/* eslint-disable */
async function showLegs() {
  try {
    if (!legslayer) {

      console.log("Build Legs layer")

      if (!legsData) {
        legsData = await dataStore.retrieveJourneyLegs(props.journey.guid)
      }

      console.log(`legsData:${JSON.stringify(legsData)}`)
      legslayer = L.layerGroup();

      for (let leg of legsData) {
        // Define the line coordinates
        legsCol = getRandomVisibleColor()
        const lines = [];

        for (let pos of leg.route) {
          lines.push([pos.gps?.lat, pos.gps?.lon]);
        }

        // Add lines to the layer
        const polyline = L.polyline(lines, { color: legsCol });
        polyline.addTo(legslayer);

        const marker1 = L.circleMarker([leg.leg.priorstop?.lat, leg.leg.priorstop?.lon], {
          radius: 5,
          color: 'black',
          fillColor: legsCol,
          fillOpacity: 1
        })
        marker1.addTo(legslayer);

        const marker2 = L.circleMarker([leg.leg.stop?.lat, leg.leg.stop?.lon], {
          radius: 5,
          color: 'black',
          fillColor: legsCol,
          fillOpacity: 1
        })
        marker2.addTo(legslayer);
      }
    } else {
      console.log("Route layer already exists")
    }

    if (legslayer) mapStore.addLayer(legslayer);

  }
  catch (error) {
    console.log(`Error fetching route data: ${error}`)
  }

}

async function showAveRoutes() {

  console.log(`showAveRoutes`)
  try {
    if (!averouteslayer) {

      console.log("Build Ave Routes layer")

      if (!legsData) {
        legsData = await dataStore.retrieveJourneyLegs(props.journey.guid)
      }

      averouteslayer = L.layerGroup();

      if (!aveRoutesData) {
        aveRoutesData = {}

        // iterate through all legs
        for (let leg of legsData) {
          console.log(`leg.ref:${leg.ref}`)

          let legRoutesArr = await dataStore.journeyaveroutes(leg.ref)
          let legRoutes = legRoutesArr[0]

          for (let ref in legRoutes.routes) {

            let rt = legRoutes.routes[ref]

            let legsCol = getRandomVisibleColor()
            const lines = [];

            for (let pos of rt.points) {
              if (pos.lat && pos.lon) {
                lines.push([pos.lat, pos.lon]);
              } else {
                console.log(`Missing lat,lon`);
              }
            }

            // Add lines to the layer
            const polyline = L.polyline(lines, { color: legsCol });
            polyline.addTo(averouteslayer);
          }
        }

      }

    } else {
      console.log("Route layer already exists")
    }

    if (averouteslayer) mapStore.addLayer(averouteslayer);

  }
  catch (error) {
    console.log(`Error fetching route data: ${error}`)
  }

}


function hideStops() {

  if (stopslayer) {
    mapStore.removeLayer(stopslayer);
  }
}

function hideRoute() {

  if (routelayer) mapStore.removeLayer(routelayer);
  if (poslayer) mapStore.removeLayer(poslayer);
}

function hideLegs() {
  if (legslayer) mapStore.removeLayer(legslayer);
}

function hideAveRoutes() {
  if (averouteslayer) mapStore.removeLayer(averouteslayer);
}


function getRandomVisibleColor() {
  const minBrightness = 0;
  const maxBrightness = 220;

  // Generate random values for red, green, and blue channels
  const r = Math.floor(Math.random() * (maxBrightness - minBrightness) + minBrightness);
  const g = Math.floor(Math.random() * (maxBrightness - minBrightness) + minBrightness);
  const b = Math.floor(Math.random() * (maxBrightness - minBrightness) + minBrightness);

  // Convert to hex and return the color
  return `rgb(${r}, ${g}, ${b})`;
}
</script>

<style scoped>
/* Add row-specific styles here if needed */
.arrow-icon .arrow {
  width: 10;
  height: 10;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 20px solid red;
  /* Triangle arrow */
}
</style>